// The lounge's screens, as one page that stays open all evening.
//
// A venue screen used to BE the space's page, so crossing a boundary was a
// page load and the machine showed whatever the next space shows while it
// loads — for kids2026 that is a black veil for up to thirteen seconds of a
// two-minute visit. Nobody standing in a gallery should watch a browser load.
//
// So the screen is a shell that never navigates, and the space rides in an
// iframe inside it. At a crossing the space asks the shell to cross; the shell
// loads the next space in a frame behind the one on screen, and when it is
// ready fades the space on screen out to its own black, tears that scene
// down, and brings the next one up out of the black. Nothing on the screen is
// ever a page that is still loading, and the black lasts about a second
// rather than the next space's whole load (twelve seconds for kids2026).
//
// Only one scene draws at a time. The frame behind runs its page — ticks,
// loads, the tour — but its renderer skips every draw until it is brought
// forward (holdDrawing). Two scenes both drawing through a crossing, on the
// wall two WebGL canvases at 7680x2160, is what made the wall flash white at
// random as a crossing finished.
//
// The shell does not join the tour. It publishes nothing, subscribes to
// nothing, and carries no guide: what it wraps is the ordinary venue page,
// unchanged, still publishing from inside as it always did.
import { TourProgram } from './TourProgram.js';
import { HYPER_TOUR_PROGRAM, stageForSpace } from './program.js';
import { loadMapSheet, mapSheet } from './hyper-map.js';
import { waypointsBetween } from './route-through.js';
import { askTheRoom } from './ask-the-room.js';
import { REBUILDS, REBUILD_KEY, REBUILD_WINDOW_MS, crossingStep } from '../lib/stuck-crossing.js';
import { mayReload } from './restore-reload.js';

import { ROLES, SCREEN_KEY, currentRole, roleValue, shellReloadAfterS } from '../lib/roles.js';
import { screenNameOf, listenForFreeze, listensForFreeze, holdAnimations, releaseAnimations, stillCopyOf, stopTheLook } from './screen-freeze.js';

const FOLLOWING_KEY = 'hyper-tour-following';
// Often enough to notice a crossing, rarely enough to cost nothing.
const WATCH_MS = 500;

const screen = document.body.dataset.screen;

// `?inset=<px>`: how much of the picture's top and bottom edges the display
// does not show (a projector's overscan, measured at install). The spaces keep
// their writing that far in from those edges; the picture itself is not moved.
// Handed on the way the screen's mark is, for the frames to read.
const INSET_KEY = 'hyper-tour-inset';
const inset = Math.max( 0, Number( new URLSearchParams( location.search ).get( 'inset' ) ) || 0 );
// `?sphere=<m>` and `?sphereBand=<m>`: the 49-inch's picture boundary and the
// depth a face fades over at it (hyper-tour-follow SPHERE_R_M), for comparing
// values on the screen. Absent, the page's own.
const SPHERE_KEY = 'hyper-tour-sphere';
const query = new URLSearchParams( location.search );
const sphere = JSON.stringify( { r: Number( query.get( 'sphere' ) ) || 0, band: Number( query.get( 'sphereBand' ) ) || 0 } );
// `?lookRadius=<m>`, `?lookTube=<m>`, `?lookRoof=<m>` and `?lookShort=<m>`: the
// wall's look-through column radius, the sight-line tube's radius (0: no tube,
// for the spaces trying one), how
// high over the floor under the stop the column starts in place of the route's
// `dollhouseRoof`, and how far before the party the tube ends
// (view-choice.js LOOK_THROUGH), for comparing values on the wall. Absent, the page's own. New names, so that an old
// `?peek=` left on a screen's address, which meant something else each time
// it changed, does nothing.
const LOOK_KEY = 'hyper-tour-look';
// `?towerTilts=<level>,<down>,<up>`: the wall's three angles on the 🚩 in the
// Tower, degrees down (hyper-tour-follow TOWER_TILTS). Absent, the page's own.
const TILTS_KEY = 'hyper-tour-tilts';
const tilts = ( query.get( 'towerTilts' ) || '' ).split( ',' ).map( Number );
const look = JSON.stringify( { radius: Math.max( 0, Number( query.get( 'lookRadius' ) ) || 0 ), tube: query.has( 'lookTube' ) ? Math.max( 0, Number( query.get( 'lookTube' ) ) || 0 ) : null, roof: Math.max( 0, Number( query.get( 'lookRoof' ) ) || 0 ), short: Math.max( 0, Number( query.get( 'lookShort' ) ) || 0 ) } );

// `?wallBack=<m>`, `?wallUp=<m>` and `?wallAhead=<m>`: how far behind the 🚩
// the wall's lens stands, how far over it, and how far past it it aims, in a
// space with a framing of its own (hyper-tour-follow SCREENS.wall.spaces).
// `?wallOrbit=cube`: the wall circles the tour's cube in the Tower, standing,
// as the tower-map does (SCREENS.wall.orbitsWhenAsked). `?mapFill=`,
// `?wallLook=` and `?wallFill=`: how the tower-map and the wall follow the
// cube the guide picked (hyper-tour-follow TOWER_FOLLOW).
const WALL_KEY = 'hyper-tour-wall';
const wall = JSON.stringify( { back: Math.max( 0, Number( query.get( 'wallBack' ) ) || 0 ), up: Math.max( 0, Number( query.get( 'wallUp' ) ) || 0 ), ahead: Math.max( 0, Number( query.get( 'wallAhead' ) ) || 0 ), orbit: query.get( 'wallOrbit' ) === 'cube' ? 'cube' : null, mapFill: query.has( 'mapFill' ) ? Number( query.get( 'mapFill' ) ) : null, look: query.has( 'wallLook' ) ? Number( query.get( 'wallLook' ) ) : null, wallFill: query.has( 'wallFill' ) ? Number( query.get( 'wallFill' ) ) : null } );

// Written when the shell loads, and again when a page that wraps it (public
// /hypertour/, which takes the marks off on its way out) comes back from the
// back-forward cache: without them the next crossing's frame would start as
// a visitor's page rather than this screen.
// Every mark this shell leaves in the tab, for a page that wraps it to take
// off again (public /hypertour/'s pagehide) — one list, here.
export const SHELL_KEYS = [ SCREEN_KEY, FOLLOWING_KEY, INSET_KEY, SPHERE_KEY, LOOK_KEY, TILTS_KEY, WALL_KEY ];

/** Take every mark off, each on its own: one key the storage refuses does
 *  not leave the others behind. */
export function unmarkTheTab() {
  for ( const key of SHELL_KEYS ) {
    try { window.sessionStorage.removeItem( key ); } catch ( _err ) { /* this one stays; the next is tried */ }
  }
}

// Not `following`: a frame with the screen's mark follows the tour by that
// mark alone (hyper-tour-follow), and `following` is the one mark a page
// outside the shell acts on — left in the tab, the next page the visitor
// opened there was taken onto the tour. The list above still takes an old
// one off.
const markTheTab = () => {
  window.sessionStorage.setItem( SCREEN_KEY, screen );
  window.sessionStorage.setItem( INSET_KEY, String( inset ) );
  window.sessionStorage.setItem( SPHERE_KEY, sphere );
  window.sessionStorage.setItem( LOOK_KEY, look );
  if ( tilts.length === 3 && tilts.every( Number.isFinite ) ) window.sessionStorage.setItem( TILTS_KEY, JSON.stringify( tilts ) );
  else window.sessionStorage.removeItem( TILTS_KEY );
  window.sessionStorage.setItem( WALL_KEY, wall );
  window.sessionStorage.removeItem( 'hyper-tour-peek' );
};
// The marks are the shell's only while it is the page: off as it goes, back
// if it is shown again from the back-forward cache.
window.addEventListener( 'pagehide', unmarkTheTab );
window.addEventListener( 'pageshow', ( e ) => {
  if ( !e.persisted ) return;
  try { markTheTab(); } catch ( _err ) { /* the same storage refused it at load */ }
} );
try {
  markTheTab();
} catch ( err ) {
  document.body.textContent =
    'This browser will not keep the screen setting, so it cannot be a venue screen.';
  throw new Error( 'hyper-tour shell: sessionStorage is unavailable', { cause: err } );
}

// `f` fullscreens the screen, as it does on kids2026's venue pages
// (kids2026-mikan-no-mikan/mikan-tree/kiosk-fullscreen.js): the window's own
// button is pointer work that misses and leaves the address bar on the wall.
// The key reaches whichever document has focus, and after a click that is the
// frame, so every frame gets the listener too; fullscreen is always the
// shell's, so the frames ride inside it.
const toggleFullscreen = ( e ) => {
  if ( e.key !== 'f' && e.key !== 'F' ) return;
  const t = e.target;
  if ( t && ( t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable ) ) return;
  if ( document.fullscreenElement ) {
    document.exitFullscreen();
  } else {
    document.documentElement.requestFullscreen( { navigationUI: 'hide' } )
      .catch( ( err ) => console.warn( `[shell] fullscreen refused: ${ err.message }` ) );
  }
};
window.addEventListener( 'keydown', toggleFullscreen );
const listenForKeys = ( f ) => f.addEventListener( 'load', () => {
  try { f.contentWindow.addEventListener( 'keydown', toggleFullscreen ); } catch ( _err ) { /* not ours */ }
} );
for ( const f of document.querySelectorAll( 'iframe' ) ) listenForKeys( f );

// Matches the CSS transition, so the old frame is removed once it is invisible.
// `?fade=<ms>` on the screen's URL sets both, `?fade=0` cutting instead of
// fading: a crossing changes the screen's opacity three times in a little over
// a second, and a display that flickers as a fullscreen picture moves between
// being scanned out directly and being composited would show exactly that.
// Cutting takes the fades out of the question.
const FADE_MS = ( () => {
  const asked = Number( new URLSearchParams( location.search ).get( 'fade' ) );
  return Number.isFinite( asked ) && new URLSearchParams( location.search ).has( 'fade' ) ? Math.max( 0, asked ) : 600;
} )();
if ( FADE_MS !== 600 ) {
  const style = document.createElement( 'style' );
  style.textContent = `.space { transition: opacity ${ FADE_MS }ms ease-out !important; }`;
  document.head.appendChild( style );
}

// The crossing's fade is drawn by a black sheet over the spaces rather than by
// the spaces' own opacity, so the space's layer stays opaque throughout and a
// crossing never asks the window to composite a translucent fullscreen
// picture. The lab's white flash went with that change. `?fade=<ms>` fades the
// spaces themselves as before (`?fade=0` cuts), for comparison.
const COVER = !new URLSearchParams( location.search ).has( 'fade' ) ||
  new URLSearchParams( location.search ).get( 'fade' ) === 'cover';
const cover = COVER ? document.createElement( 'div' ) : null;
if ( cover ) {
  const style = document.createElement( 'style' );
  style.textContent = `.space { transition: none !important; }
    #space-cover { position: fixed; inset: 0; background: #000; opacity: 1; pointer-events: none;
      z-index: 10; transition: opacity 600ms ease-out; }`;
  document.head.appendChild( style );
  cover.id = 'space-cover';
  document.body.appendChild( cover );
  // While the cover is up — the first load, every crossing — a visitor's
  // browser says what is happening; a venue screen stays black (roles.js
  // loading). In the middle of /hypertour/'s circle (--hole-y), else of the
  // screen.
  if ( roleValue( 'loading' ) === 'show' ) {
    const word = document.createElement( 'p' );
    word.className = 'space-loading';
    word.textContent = 'Loading...';
    Object.assign( word.style, {
      position: 'absolute', left: '50%', top: 'calc( var( --hyper-runway, 0px ) + var( --hole-y, 50vh ) )', margin: '0',
      transform: 'translate( -50%, -50% )', color: '#888', font: '13px/1 sans-serif',
    } );
    cover.appendChild( word );
  }
}
const lift = () => {
  if ( !cover ) return;
  requestAnimationFrame( () => requestAnimationFrame( () => { cover.style.opacity = '0'; } ) );
};

// The last way out of a crossing that never arrives.
//
// Staying in the space the tour has left beats showing a gallery that has not
// come up, so a crossing waits for the frame behind to be ready and for nothing
// else — but staying is not the same as stopping, and it stopped: a screen was
// measured holding the Tower for ten minutes with the theatre loading behind
// it. What was supposed to rescue it was the next boundary, and the next
// boundary never comes, because the page that announces one is the page ON
// SCREEN and it announces exactly once. Once its crossing is stuck it has
// nothing left to say.
//
// So a minute, and then the screen moves whether or not the frame says it is
// ready. Long enough that it is not the twenty seconds this replaced — that one
// fired on ordinary slowness and put loading pages on the wall — and short
// enough that it is a pause rather than an evening. Nothing before it changes:
// a crossing still swaps the moment the frame has drawn.
const STUCK_LIMIT_MS = 60000;

let front = document.getElementById( 'space' );
let back = null;
let watching = null;
// Where the frames go: the body on the venue's screens, the page's stage on
// /hypertour/ (lib/edge-to-edge.js). Kept rather than read off `front`,
// which is null from a fade-out until the next swap: a crossing retried in
// that gap still needs somewhere to put its frame.
let stage = front.parentNode || document.body;
const stageOf = () => {
  if ( front && front.parentNode ) stage = front.parentNode;
  return stage;
};

// Is the page in this frame still behind its veil? Not `offsetParent`: the veil
// is position:fixed and a fixed element's offsetParent is null whether or not
// it is on screen.
const stillLoading = ( f ) => {
  try {
    // A frame that has only just been given a src is still about:blank, and
    // about:blank is `complete` and has no veil — so asking the veil alone
    // says "ready" and the shell swaps to an empty rectangle. It is only ready
    // when it is showing the page it was sent to.
    const want = f.dataset.page;
    const here = f.contentWindow && f.contentWindow.location
      && f.contentWindow.location.pathname;
    if ( !here || here === 'blank' || here === 'about:blank' ) return true;
    if ( want && here !== want ) return true;
    const doc = f.contentDocument;
    if ( !doc || doc.readyState !== 'complete' ) return true;
    const veil = doc.getElementById( 'loading' );
    if ( !veil ) return false;
    const cs = f.contentWindow.getComputedStyle( veil );
    return cs.display !== 'none' && cs.visibility !== 'hidden'
      && parseFloat( cs.opacity || '1' ) > 0.01;
  } catch ( _err ) {
    return true;   // mid-navigation, or not there yet
  }
};

// A veil that has gone is not a frame that has been drawn, and the last veils
// that reached a screen at a crossing were in the gap between the two — under
// a second each, which is why raising the limit above would not have touched
// them. So once a frame is showing its page unveiled, ask that frame's own
// requestAnimationFrame twice: the first callback runs before the next paint,
// the second cannot run until that paint has happened. The scene's render loop
// is on the same clock and was registered first, so by the second callback it
// has drawn. One callback is not enough — it can still fire in the frame the
// veil was removed in, before anything has been painted.
const drawn = new WeakMap();   // iframe -> { win, done }

const hasDrawn = ( f ) => {
  const win = f.contentWindow;
  const counting = drawn.get( f );
  // Tied to the window it was started in: a frame that navigated under the
  // count would otherwise be waiting on paints of a page it has left, which
  // never come.
  if ( counting && counting.win === win ) return counting.done;
  const state = { win, done: false };
  drawn.set( f, state );
  try {
    win.requestAnimationFrame(
      () => win.requestAnimationFrame( () => { state.done = true; } ) );
  } catch ( _err ) {
    drawn.delete( f );   // mid-navigation; start the count again next tick
  }
  return false;
};

// A space lifts its veil before it is looking at anything. Until the tour is
// heard, the camera stands where the page puts it, and kids2025's scan comes
// in after that: what is drawn meanwhile is a light grey sky over an orange
// floor — on a 4K screen in a dark lounge, the whole screen going white for
// up to a second and a half at every arrival. So a frame waits, beyond its
// veil, until its camera is on the tour (when the tour is in that space) and
// its scan, if it has one, has something in view — for SETTLE_WAIT_MS at
// most, after which an unsettled room is still better than a stuck screen.
const SETTLE_WAIT_MS = 8000;
const settleFrom = new WeakMap();   // iframe -> when its veil went
const settled = ( f ) => {
  try {
    const doc = f.contentDocument;
    if ( !settleFrom.has( f ) ) settleFrom.set( f, Date.now() );
    if ( Date.now() - settleFrom.get( f ) > SETTLE_WAIT_MS ) return true;
    const followEl = doc.querySelector( '[hyper-tour-follow]' );
    const follow = followEl && followEl.components && followEl.components[ 'hyper-tour-follow' ];
    // Until the tour has posed its camera (hyper-tour-follow `posed`), before
    // the tour is first heard too: the frame came forward on the page's
    // default view and then jumped to the tour.
    if ( follow && follow.screen && follow.screen.follows !== false && !follow.posed ) return false;
    const cloudEl = doc.querySelector( '[potree-cloud]' );
    if ( !cloudEl ) return true;
    const cloud = cloudEl.components && cloudEl.components[ 'potree-cloud' ];
    const pco = cloud && cloud._pointClouds && cloud._pointClouds[ 0 ];
    return !!( pco && pco.visibleNodes && pco.visibleNodes.length );
  } catch ( _err ) {
    return true;
  }
};

// Ready to be seen: showing the page it was sent to, its veil gone, settled
// (see above), and a frame painted since.
const ready = ( f ) => {
  if ( stillLoading( f ) ) { drawn.delete( f ); return false; }
  if ( !settled( f ) ) { drawn.delete( f ); return false; }
  return hasDrawn( f );
};

// Bring `f` forward once it is ready — and not before, unless it never is.
// The same rule serves a crossing and the first load: what this screen shows is
// either a space with something in it or the shell's own black, never a page on
// its way.
//
// The minute is the floor, not the schedule (STUCK_LIMIT_MS says why). Ordinary
// slowness is waited out: kids2026 cold was measured at seventeen seconds and
// swapped when it had drawn, not on a clock.
// A space that does not come up is loaded again (`retry`), and then the
// shell is rebuilt where the tour is now (lib/stuck-crossing.js): a screen does
// not stand on "Loading" until the next crossing. The minute above stays as
// the last way out, past the rebuilds' bound.
let crossingRetries = 0;
// Refused once, the bound is not asked again (nor the refusal logged) until
// the window it counts has passed.
let rebuildRefusedAt = null;
const rebuild = ( f ) => {
  if ( rebuildRefusedAt !== null && Date.now() - rebuildRefusedAt < REBUILD_WINDOW_MS ) return false;
  rebuildRefusedAt = null;
  let stamps = [];
  try { stamps = JSON.parse( window.sessionStorage.getItem( REBUILD_KEY ) || '[]' ); } catch ( _err ) { stamps = []; }
  const now = Date.now();
  const { allowed, recent } = mayReload( stamps, now, REBUILDS, REBUILD_WINDOW_MS );
  if ( !allowed ) {
    rebuildRefusedAt = now;
    console.warn( `[shell] stuck ${ f.dataset.page }; ${ recent.length } rebuilds in ${ REBUILD_WINDOW_MS / 1000 } s already, waiting on` );
    return false;
  }
  try {
    window.sessionStorage.setItem( REBUILD_KEY, JSON.stringify( [ ...recent, now ] ) );
  } catch ( _err ) {
    return false;   // nothing counted, so no rebuild: the bound must hold
  }
  console.warn( `[shell] stuck ${ f.dataset.page } after a retry; rebuilding where the tour is` );
  window.location.reload();
  return true;
};
// When the frame's page last had something arrive (its newest resource), as
// a time: a space still coming in is not one that has failed
// (lib/stuck-crossing.js crossingStep).
const arrivedLast = ( f ) => {
  try {
    const perf = f.contentWindow.performance;
    const last = perf.getEntriesByType( 'resource' ).reduce( ( m, e ) => Math.max( m, e.responseEnd ), 0 );
    return last ? perf.timeOrigin + last : null;
  } catch ( _err ) {
    return null;   // mid-navigation
  }
};
const showWhenReady = ( f, show, retry = null ) => {
  let opened = Date.now();
  let veilGoneAt = null;
  clearInterval( watching );
  watching = setInterval( () => {
    if ( !f.isConnected ) { clearInterval( watching ); return; }
    // A hidden window (behind another, a display asleep) paints nothing, so no
    // frame in it can be ready, and the waits below would run out on a space
    // that is fine: retried, rebuilt, and past the bound left showing. They
    // start again when the window is seen.
    if ( document.hidden ) { opened = Date.now(); veilGoneAt = null; return; }
    if ( ready( f ) ) {
      clearInterval( watching );
      crossingRetries = 0;
      show();
      return;
    }
    if ( veilGoneAt === null && !stillLoading( f ) ) veilGoneAt = Date.now();
    const step = retry ? crossingStep( { since: opened, veilGoneAt, progressAt: arrivedLast( f ), retries: crossingRetries }, Date.now() ) : 'wait';
    if ( step === 'retry' ) {
      clearInterval( watching );
      crossingRetries++;
      console.warn( `[shell] ${ f.dataset.page } not ready after ${ ( ( Date.now() - opened ) / 1000 ).toFixed( 0 ) } s (${ veilGoneAt === null ? 'still loading' : 'unveiled, not settled' }); loading it again (${ crossingRetries })` );
      retry();
      return;
    }
    if ( step === 'rebuild' && rebuild( f ) ) { clearInterval( watching ); return; }
    if ( Date.now() - opened > STUCK_LIMIT_MS ) {
      clearInterval( watching );
      crossingRetries = 0;
      show();
    }
  }, WATCH_MS );
};

// Make a frame's scene skip its draws until released. Everything else about
// the page runs — its ticks load the space and follow the tour — so what comes
// forward is a space that is ready, not one that starts from where it paused.
// Retried until the frame's scene has a renderer; a few frames drawn before
// that are the only overlap there is.
// `mark` is what the frame is told it is (`held` behind, or `frozen` on
// screen): a page that reads `held` (hyper-tour-follow _heldBehind) takes
// itself for a frame behind and never asks to cross.
const holdDrawing = ( f, mark = 'held' ) => {
  const hold = () => {
    if ( !f.isConnected || f.dataset.held === 'released' ) return;
    try {
      const scene = f.contentDocument && f.contentDocument.querySelector( 'a-scene' );
      const renderer = scene && scene.renderer;
      // Already wrapped (a frame held once, now held again): the wrapper
      // reads the flag, which is set.
      if ( renderer && renderer.heldByShell ) return;
      if ( renderer ) {
        const draw = renderer.render.bind( renderer );
        renderer.render = ( ...args ) => { if ( f.dataset.held !== 'held' && f.dataset.held !== 'frozen' ) draw( ...args ); };
        renderer.heldByShell = true;
        return;
      }
    } catch ( _err ) { /* mid-navigation */ }
    setTimeout( hold, 50 );
  };
  f.dataset.held = mark;
  hold();
};

// Say when a frame's WebGL context is lost or comes back, and show the
// shell's black instead of the frame while it is lost. A lost context is how a
// GPU that has run out shows itself, and the lounge's Mac minis draw 4K; the
// log line, with its time and the space, is what the screens' watch reads.
const watchContext = ( f ) => {
  const attach = () => {
    if ( !f.isConnected ) return;
    let canvas = null;
    try { canvas = f.contentDocument && f.contentDocument.querySelector( 'canvas.a-canvas' ); } catch ( _err ) { /* mid-navigation */ }
    if ( !canvas ) { setTimeout( attach, 200 ); return; }
    const say = ( what ) => console.warn(
      `[shell] ${ what } ${ new Date().toISOString() } ${ f.dataset.page } ${ f.dataset.held || 'front' }` );
    canvas.addEventListener( 'webglcontextlost', () => { say( 'webglcontextlost' ); f.style.visibility = 'hidden'; } );
    canvas.addEventListener( 'webglcontextrestored', () => { say( 'webglcontextrestored' ); f.style.visibility = ''; } );
  };
  attach();
};

// Two opt-in variants for finding the lab's white flash, which comes after a
// crossing on the screens that cross and never on the one that does not.
// Each moves one of a crossing's changes to the window's layers away from the
// others, so the flash's time against the swap (the watch logs both, from the
// [shell] lines below) says which one it follows:
//   ?swap=late  the space the tour has left stops drawing at the swap but is
//               torn down SWAP_LATE_MS later, not at it — its WebGL context
//               goes away on its own, well clear of the fade and first draw;
//   ?hold=off   the next space draws while it loads behind instead of being
//               held until the swap, so nothing starts drawing at the swap.
const QUERY = new URLSearchParams( location.search );
const SWAP_LATE = QUERY.get( 'swap' ) === 'late';
const SWAP_LATE_MS = 6000;
const HOLD_OFF = QUERY.get( 'hold' ) === 'off';
const mark = ( what, f ) => console.warn(
  `[shell] ${ what } ${ new Date().toISOString() } ${ f ? f.dataset.page : '' }` );

// The arriving space draws again and fades up out of the shell's black.
const swap = ( arriving ) => {
  if ( arriving !== back ) return;   // overtaken while the old one faded
  front = back;
  back = null;
  mark( 'swap', front );
  front.dataset.held = 'released';
  front.style.opacity = '1';
  lift();
  // Held as it came forward (a crossing already under way when the freeze
  // came): it draws its first frames, so what stands is the space, not the
  // black it came up out of.
  if ( frozen ) {
    const arrived = front;
    try {
      arrived.contentWindow.requestAnimationFrame( () => arrived.contentWindow.requestAnimationFrame( () => {
        if ( frozen && front === arrived ) freezeFrame( arrived );
      } ) );
    } catch ( _err ) { /* gone again */ }
  }
};

// Let a frame's scene go before the frame does. about:blank unloads the page,
// and with it the WebGL context, now rather than whenever the frame is
// collected.
const tearDown = ( f ) => {
  if ( !f ) return;
  if ( SWAP_LATE && f.isConnected && f.dataset.late !== 'due' ) {
    // Stops drawing now (the same hold a loading frame is under), goes later.
    f.dataset.late = 'due';
    holdDrawing( f );
    setTimeout( () => tearDown( f ), SWAP_LATE_MS );
    return;
  }
  mark( 'teardown', f );
  try { f.src = 'about:blank'; } catch ( _err ) { /* already gone */ }
  f.remove();
};

// Fade the space on screen out to black and tear it down, then call `then`.
// A crossing asked for while one is already fading waits for the same black.
let fading = null;
const fadeOut = ( then ) => {
  if ( fading ) { fading.then = then; return; }
  const leaving = front;
  front = null;
  if ( !leaving || leaving.style.opacity !== '1' ) { tearDown( leaving ); then(); return; }
  fading = { then };
  if ( cover ) cover.style.opacity = '1';
  else leaving.style.opacity = '0';
  setTimeout( () => {
    tearDown( leaving );
    const next = fading.then;
    fading = null;
    next();
  }, FADE_MS );
};

// A crossing to the space already opened behind (`prepare`) swaps to it as
// soon as it is ready; to any other, opens it now.
const crossTo = ( page ) => {
  if ( back && back.dataset.prepared && back.dataset.page === page ) {
    delete back.dataset.prepared;
    const arriving = back;
    const early = setTimeout( () => { if ( back === arriving ) fadeOut( () => {} ); }, LEAVE_BY_MS );
    showWhenReady( arriving, () => { clearTimeout( early ); fadeOut( () => swap( arriving ) ); },
      () => { clearTimeout( early ); openBehind( page ); } );
    return;
  }
  openBehind( page );
};
// How long the space the tour has left stays on screen while the next loads.
const LEAVE_BY_MS = 6000;

// The next space, opened behind the one on screen and held there (it draws
// nothing and, being a screen, publishes nothing) until the tour crosses.
const prepare = ( page ) => {
  if ( back ) return;
  back = document.createElement( 'iframe' );
  back.className = 'space';
  back.setAttribute( 'title', document.title );
  back.setAttribute( 'allow', 'autoplay; xr-spatial-tracking' );
  back.style.opacity = '0';
  back.dataset.page = page;
  back.dataset.prepared = '1';
  listenForKeys( back );
  holdDrawing( back );
  watchContext( back );
  stageOf().appendChild( back );
  back.src = page;
  mark( 'prepare', back );
};

const openBehind = ( page ) => {
  if ( back ) {
    // A crossing overtaken by the next one. The frame on its way is loading a
    // space the tour has already left, so it goes and the new destination opens
    // in its place; letting it finish would put the wrong gallery on the screen
    // at the end of a wait. This is the first way out of a stuck crossing and
    // the better one, because it fires on the tour moving on; the minute above
    // is the second, for when this screen is the one that is stuck and has
    // therefore stopped being told anything.
    clearInterval( watching );
    tearDown( back );
    back = null;
  }
  back = document.createElement( 'iframe' );
  back.className = 'space';
  back.setAttribute( 'title', document.title );
  back.setAttribute( 'allow', 'autoplay; xr-spatial-tracking' );
  back.style.opacity = '0';
  back.dataset.page = page;
  listenForKeys( back );
  if ( !HOLD_OFF ) holdDrawing( back );
  watchContext( back );
  stageOf().appendChild( back );
  back.src = page;
  const arriving = back;
  // A long load is not waited out on the space the tour has left: after
  // LEAVE_BY_MS the old space fades to black while it is still moving, and
  // the new one comes up out of that black when it is ready.
  const early = setTimeout( () => { if ( back === arriving ) fadeOut( () => {} ); }, LEAVE_BY_MS );
  showWhenReady( arriving, () => { clearTimeout( early ); fadeOut( () => swap( arriving ) ); },
    () => { clearTimeout( early ); openBehind( page ); } );
};

// The space asks to cross; this page is the one that knows how.
window.addEventListener( 'message', ( e ) => {
  if ( e.origin !== window.location.origin ) return;
  if ( !e.data || e.data.type !== 'hyper-tour-cross' || !e.data.page ) return;
  // Only the space on screen says where the tour went: a space opened behind
  // it ahead of the tour (prepare) hears the tour elsewhere and would ask to
  // go back.
  if ( front && e.source !== front.contentWindow ) return;
  if ( frozen ) { crossWhileFrozen( e.data.page ); return; }
  if ( reloadsAt( e.data.page ) ) { reloadInTheBlack( e.data.page ); return; }
  crossTo( e.data.page );
} );

// At the arrival roles.js names (`reloadAt`, the wall's), once the shell has
// been up `reloadAfterS`, it reloads in the crossing's black instead of
// crossing: the wall, after
// some hours of crossings, misses a frame about once a second, and a reload
// is what clears it. The black is the space on screen faded out, then the
// shell and that one space loaded cold; reopened, the shell asks the room
// where the tour is as it does on any opening. Never frozen: a freeze over
// the arrival lets it go, and the next lap's arrival reloads.
// The shell's own row: roleValue here would read the tab's top page, which
// roles.js takes for a desktop (screenMark believes the mark only in a frame).
const SHELL_ROW = ROLES[ `${ document.documentElement.dataset.tree === 'venue' ? 'venue' : 'public' }-${ screen }` ] || {};
const RELOAD_AT = SHELL_ROW.reloadAt || null;
// Only a shell that has been up this long (`reloadAfterS`, or the address's
// `?reload-after=`): each reload is seconds of black, so a few a day rather
// than one a lap.
const RELOAD_AFTER_S = shellReloadAfterS( SHELL_ROW, location.search );
const reloadsAt = ( page ) => !!RELOAD_AT && RELOAD_AFTER_S !== null && page === RELOAD_AT
  && performance.now() >= RELOAD_AFTER_S * 1000;
const reloadInTheBlack = ( page ) => fadeOut( () => {
  if ( frozen ) { openBehind( page ); return; }
  mark( 'reload', null );
  location.reload();
} );

// The freeze (screen-freeze.js): the space on screen stops where it is — it
// draws nothing more (holdDrawing) and its scene is paused, so its camera, the
// party, the faces, the words over them, the scan and the sky all stand still
// in the last picture drawn; drawing alone held, the words and labels the
// page moves in its ticks went on moving over the still scene. The page goes
// on hearing the tour.
//
// Given back, it jumps to where the tour is now (`hyper-tour:resumed`, which
// hyper-tour-follow answers by placing its camera afresh) rather than racing
// through what it missed, and with nothing in between — no veil, no black.
// A crossing the tour made meanwhile is remembered, its space opened behind
// and held there like any space opened ahead (prepare), and the screen cuts
// to it when given back: the frozen picture stays until that space is ready.
// The space behind is held and so says nothing of the tour moving on again;
// the room is asked instead, while frozen and on being given back, and the
// latest answer wins. A space is never open twice: two frames of one space
// are one screen in its room twice. So a tour that has come all the way
// round to the frozen space goes the one way that has a black in it: the
// frozen picture out, then the space opened afresh.
let frozen = false;
let crossedWhileFrozen = null;
let askingWhileFrozen = null;
// How often the room is asked where the tour is, once it has left the frozen
// space.
const FROZEN_ASK_MS = 15000;

const sceneOf = ( f ) => {
  try { return f.contentDocument && f.contentDocument.querySelector( 'a-scene' ); } catch ( _err ) { return null; }
};

const freezeFrame = ( f ) => {
  if ( !f ) return;
  holdDrawing( f, 'frozen' );
  const scene = sceneOf( f );
  if ( scene && scene.isPlaying ) scene.pause();
  try { holdAnimations( f.contentDocument ); } catch ( _err ) { /* gone */ }
  try { stopTheLook( f.contentWindow ); } catch ( _err ) { /* gone */ }
};

const thawFrame = ( f ) => {
  if ( !f ) return;
  f.dataset.held = 'released';
  const scene = sceneOf( f );
  if ( scene && !scene.isPlaying ) scene.play();
  try { releaseAnimations( f.contentDocument ); } catch ( _err ) { /* gone */ }
  try { f.contentWindow.dispatchEvent( new CustomEvent( 'hyper-tour:resumed' ) ); } catch ( _err ) { /* gone */ }
};

// The space behind made `page`'s, or none when `page` is the one frozen on
// screen.
const preparedFor = ( page ) => {
  if ( back && back.dataset.page === page ) return;
  if ( back ) { clearInterval( watching ); tearDown( back ); back = null; }
  if ( page && !( front && front.dataset.page === page ) ) prepare( page );
};

const crossWhileFrozen = ( page ) => {
  crossedWhileFrozen = page;
  preparedFor( page );
  if ( askingWhileFrozen ) return;
  askingWhileFrozen = setInterval( () => {
    whereTheTourIs().then( ( stage ) => { if ( frozen && stage && stage.page ) preparedFor( stage.page ); } );
  }, FROZEN_ASK_MS );
};

// Cut, not fade: the frozen picture goes and the space behind is there.
const cutTo = ( arriving ) => {
  if ( arriving !== back ) return;
  const leaving = front;
  front = null;
  tearDown( leaving );
  swap( arriving );
};

// The shell's other frames — the tower-map's plan (#map) — are pages of
// their own that hear the room and draw it in their DOM, which neither the
// hold nor the pause reaches. Each stops where it is (its animation frames
// held back) and is covered by a copy of itself as it stood: its DOM without
// scripts, in a frame that runs none (sandboxed; same origin, so its
// stylesheets and fonts load), shown in its place once the copy is ready.
// The live page goes on hearing the tour underneath, so given back it is
// already where the tour is. `still` marks a copy, `space` a space frame.
const stills = new Map();

const othersOf = () => [ ...document.querySelectorAll( 'iframe' ) ]
  .filter( ( f ) => !f.classList.contains( 'space' ) && !f.classList.contains( 'still' ) );

const holdFrames = ( w ) => {
  const raf = w.requestAnimationFrame;
  const waiting = [];
  w.requestAnimationFrame = ( fn ) => { waiting.push( fn ); return 0; };
  return () => {
    w.requestAnimationFrame = raf;
    for ( const fn of waiting ) raf.call( w, fn );
  };
};

const coverWithStill = ( f ) => {
  if ( stills.has( f ) ) return;
  let html = null, release = () => {};
  try {
    html = stillCopyOf( f.contentDocument );
    release = holdFrames( f.contentWindow );
  } catch ( _err ) { /* not ours, or gone */ }
  if ( !html ) return;
  const r = f.getBoundingClientRect();
  const copy = document.createElement( 'iframe' );
  copy.className = 'still';
  copy.setAttribute( 'sandbox', 'allow-same-origin' );
  copy.setAttribute( 'aria-hidden', 'true' );
  copy.style.cssText = `position: fixed; left: ${ r.left }px; top: ${ r.top }px; width: ${ r.width }px; height: ${ r.height }px;
    border: 0; margin: 0; padding: 0; background: transparent; pointer-events: none; visibility: hidden;`;
  stills.set( f, { copy, release } );
  copy.addEventListener( 'load', () => {
    let fonts = null;
    try { fonts = copy.contentDocument.fonts.ready; } catch ( _err ) { /* none */ }
    Promise.resolve( fonts ).then( () => requestAnimationFrame( () => {
      const still = stills.get( f );
      if ( !still || still.copy !== copy ) return;   // given back before it was ready
      copy.style.visibility = 'visible';
      f.style.visibility = 'hidden';
    } ) );
  }, { once: true } );
  copy.srcdoc = html;
  f.after( copy );
};

const uncover = () => {
  for ( const [ f, { copy, release } ] of stills ) {
    f.style.visibility = '';
    copy.remove();
    try { release(); } catch ( _err ) { /* gone */ }
  }
  stills.clear();
};

const freeze = () => {
  frozen = true;
  mark( 'frozen', front );
  freezeFrame( front );
  othersOf().forEach( coverWithStill );
};

const unfreeze = () => {
  frozen = false;
  mark( 'given back', front );
  uncover();
  clearInterval( askingWhileFrozen );
  askingWhileFrozen = null;
  const crossed = crossedWhileFrozen;
  crossedWhileFrozen = null;
  if ( !crossed ) { thawFrame( front ); return; }
  // The space on screen has asked to leave and will not ask again: it goes,
  // to wherever the tour is now.
  whereTheTourIs().then( ( stage ) => {
    if ( frozen ) { crossedWhileFrozen = crossed; return; }   // held again meanwhile
    const page = ( stage && stage.page ) || crossed;
    if ( front && front.dataset.page === page ) {
      if ( back ) { tearDown( back ); back = null; }
      fadeOut( () => openBehind( page ) );
      return;
    }
    preparedFor( page );
    delete back.dataset.prepared;
    const arriving = back;
    showWhenReady( arriving, () => cutTo( arriving ), () => openBehind( page ) );
  } );
};

// Only a venue screen's shell listens (a venue-* role: the venue build's
// tree), and only on its own origin.
if ( listensForFreeze( currentRole() ) ) {
  listenForFreeze( screenNameOf( screen ), ( held ) => ( held ? freeze() : unfreeze() ) );
}

// Where to open. The tour is asked first and the clock is the fallback, the
// same order the entry pages use (spec/hyper-map/tour-state.md).
const sheets = Promise.all(
  HYPER_TOUR_PROGRAM.map( ( stage ) => loadMapSheet( stage.space, stage.sheet ) ) );

const fromTheClock = () => sheets.then( () => new TourProgram( HYPER_TOUR_PROGRAM, {
  sheetFor: mapSheet,
  waypoints: ( stage, from, to ) =>
    waypointsBetween( mapSheet( stage.space ), from.position, to.position ),
} ).sample( Date.now() ).stage );

// Screens that stand in one space rather than travelling with the tour, and
// the space each of them stands in. They open it and never ask: the tour
// appears on them when the tour is there, and the rest of the time the space
// is what they are for. Every other screen follows, and asks the room where
// the tour is before falling back to the clock.
const STANDS_IN = {
  'tower-map': 'tower',
};

const standing = STANDS_IN[ screen ];

// Opening the next space early (lib/roles.js shellPreloadS): by the clock,
// which is the guide's own plan; a crossing the clock got wrong opens the
// right space the usual way and lets the prepared one go.
const PRELOAD_MS = ( roleValue( 'shellPreloadS' ) || 0 ) * 1000;
if ( PRELOAD_MS > 0 && !standing ) {
  sheets.then( () => {
    const program = new TourProgram( HYPER_TOUR_PROGRAM, {
      sheetFor: mapSheet,
      waypoints: ( stage, from, to ) => waypointsBetween( mapSheet( stage.space ), from.position, to.position ),
    } );
    setInterval( () => {
      if ( back || !front || front.style.opacity !== '1' ) return;
      let t = Date.now() % program.cycleMs;
      const stages = program.stages;
      let i = 0;
      while ( i < stages.length - 1 && t >= stages[ i ].walk.cycleMs ) { t -= stages[ i ].walk.cycleMs; i++; }
      const next = stages[ ( i + 1 ) % stages.length ];
      if ( stages[ i ].walk.cycleMs - t > PRELOAD_MS ) return;
      const page = next.page;
      if ( page && page !== front.dataset.page ) prepare( page );
    }, 5000 );
  } );
}
// Seen again after being hidden (behind another window, a display asleep):
// the tour has moved on while nothing here could be ready, and waiting out a
// retry and a rebuild from here is most of a minute of black. So the wait on
// the frame behind stops, the room is asked where the tour is, and that space
// is opened now — unless the frame behind is already it, which the browser
// went on loading while hidden and which only has to draw. Once per return,
// so it cannot run away. A frame prepared ahead is the next crossing's, not a
// wait.
const whereTheTourIs = () => ( standing
  ? Promise.resolve( stageForSpace( standing ) )
  : askTheRoom().then( ( space ) => ( space && stageForSpace( space ) ) || fromTheClock() ) );
document.addEventListener( 'visibilitychange', () => {
  if ( document.hidden || !back || back.dataset.prepared ) return;
  const was = back;
  clearInterval( watching );
  whereTheTourIs().then( ( stage ) => {
    if ( back !== was ) return;   // a crossing since, with a wait of its own
    const page = stage && stage.page;
    crossingRetries = 0;
    if ( !page || page === was.dataset.page ) {
      showWhenReady( was, () => fadeOut( () => swap( was ) ), () => openBehind( was.dataset.page ) );
      return;
    }
    mark( 'seen again; opening where the tour is', was );
    openBehind( page );
  } );
} );

const opening = whereTheTourIs();

// The first space arrives by the same rule as every one after it: the frame
// is transparent in the stylesheet, the shell's black is what stands on the
// screen until then, and `dataset.page` is what stillLoading compares against
// — without it the very first about:blank reads as ready.
opening.then( ( stage ) => {
  front.dataset.page = stage.page;
  watchContext( front );
  const load = () => {
    front.src = stage.page;
    showWhenReady( front, () => { front.style.opacity = '1'; lift(); },
      () => { front.src = 'about:blank'; load(); } );
  };
  load();
} );
